/**
 * Firestore-backed RTK Query API for the kantoordag day view.
 *
 * Replaces the REST polling in DayOverview (useGetKantoordagOnDateQuery) with a
 * live onSnapshot listener on the Firestore view collection.  The listener also
 * reads the per-building defaults once so that companies / rooms that have no
 * activity for a given day still appear with empty counts.
 *
 * Firestore paths (Kotlin server side):
 *   Day data  : Kantoordag/kd{yyyyMMdd}/{building}/  (overview, {company}, ROOM-*, waitlist)
 *   Defaults  : views/defaults/{building}/           (same document layout)
 */

import { collection, CollectionReference, Firestore, QuerySnapshot } from 'firebase/firestore';
import {
    CompanyKantoordagView,
    KantoordagView,
    KantoordagViewResponse,
    Periode,
    UserKantoordagView,
} from '../kantoordagApi';
import { createFirestoreStreamApi } from '../firestoreModel';
import { kantoordagDateKey, toIsoDate } from './firestoreUtils';

// ---------------------------------------------------------------------------
// Firestore document IDs (mirrors the Kotlin constants)
// ---------------------------------------------------------------------------

const OVERVIEW_DOC = 'overview';
const WAITLIST_DOC = 'waitlist';
const ROOM_PREFIX = 'ROOM-';

// ---------------------------------------------------------------------------
// Firestore → TypeScript mappers
// ---------------------------------------------------------------------------

type RawDoc = Record<string, unknown>;

function mapUserView(raw: RawDoc): UserKantoordagView {
    return {
        date: toIsoDate(raw.date),
        building: raw.building as string ?? '',
        userId: raw.userId as string ?? '',
        userName: raw.userName as string ?? '',
        picture: (raw.picture as string | null | undefined) ?? null,
        requestedParkingspot: (raw.requestedParkingspot as boolean) ?? false,
        periode: raw.periode as Periode,
        hasWorkspot: (raw.hasWorkspot as boolean) ?? false,
        assignedParkingspot: (raw.assignedParkingspot as boolean) ?? false,
    };
}

function mapCompanyView(docId: string, raw: RawDoc): CompanyKantoordagView {
    return {
        date: toIsoDate(raw.date),
        building: raw.building as string ?? '',
        companyName: (raw.companyName as string) ?? docId,
        companyLogo: (raw.companyLogo as string) ?? '',
        totalReservations: (raw.totalReservations as number) ?? 0,
        users: ((raw.users as RawDoc[]) ?? []).map(mapUserView),
    };
}

function mapOverviewView(raw: RawDoc): KantoordagView {
    return {
        date: toIsoDate(raw.date),
        building: raw.building as string ?? '',
        capacity: (raw.capacity as number) ?? 0,
        seatsAvailable: (raw.seatsAvailable as number) ?? 0,
        reservations: (raw.reservations as number) ?? 0,
        spotReservations: (raw.spotReservations as number) ?? 0,
        assignedSpots: (raw.assignedSpots as number) ?? 0,
        waitlistSize: (raw.waitlistSize as number) ?? 0,
        capacityPercentage: (raw.capacityPercentage as number) ?? 0,
    };
}

// ---------------------------------------------------------------------------
// Snapshot → KantoordagViewResponse
// ---------------------------------------------------------------------------

const emptyResponse = (): KantoordagViewResponse => ({
    summary: {
        date: '',
        building: '',
        capacity: 0,
        seatsAvailable: 0,
        reservations: 0,
        spotReservations: 0,
        assignedSpots: 0,
        waitlistSize: 0,
        capacityPercentage: 0,
    },
    companies: {},
});

function mapData(
    snapshot: QuerySnapshot,
    defaultsSnapshot: QuerySnapshot | null,
): KantoordagViewResponse {
    let summary: KantoordagView | null = null;
    const companies: Record<string, CompanyKantoordagView> = {};

    // 1. Read real data for the day
    snapshot.forEach((doc) => {
        const raw = doc.data() as RawDoc;
        if (doc.id === OVERVIEW_DOC) {
            summary = mapOverviewView(raw);
        } else if (doc.id !== WAITLIST_DOC && !doc.id.startsWith(ROOM_PREFIX)) {
            companies[doc.id] = mapCompanyView(doc.id, raw);
        }
        // ROOM-* and waitlist docs are not needed by DayOverview
    });

    // 2. Fill in defaults for anything missing
    if (defaultsSnapshot) {
        defaultsSnapshot.forEach((doc) => {
            const raw = doc.data() as RawDoc;
            if (doc.id === OVERVIEW_DOC) {
                if (!summary) {
                    summary = mapOverviewView(raw);
                }
            } else if (doc.id !== WAITLIST_DOC && !doc.id.startsWith(ROOM_PREFIX)) {
                if (!companies[doc.id]) {
                    companies[doc.id] = mapCompanyView(doc.id, raw);
                }
            }
        });
    }

    return { summary: summary ?? emptyResponse().summary, companies };
}

// ---------------------------------------------------------------------------
// RTK Query API
// ---------------------------------------------------------------------------

type DayViewArg = { date: string; building: string };

export const kantoordagDayViewStreamingApi = createFirestoreStreamApi<KantoordagViewResponse, DayViewArg>({
    reducerPath: 'kantoordagDayViewApi',

    getRef: (db: Firestore, { date, building }: DayViewArg): CollectionReference =>
        collection(db, 'Kantoordag', kantoordagDateKey(date), building),

    getDefaultsRef: (db: Firestore, { building }: DayViewArg): CollectionReference =>
        collection(db, 'views', 'defaults', building),

    mapData,
    emptyResult: emptyResponse,
});

export const useGetKantoordagDayViewQuery = kantoordagDayViewStreamingApi.endpoints.get.useQuery;
